import React from 'react';
import { Clock } from 'lucide-react';
import './QuizReview.css';
import OptionText from './OptionText';
import QuestionText from './QuestionText';
import AnalogClock from './questions/AnalogClock';

const QuizReview = ({ questions, userAnswers, onClickRestart }) => {
    return (
        <div className="review-container">
            <div className="review-header">
                <div>
                    <h1>Quiz Review</h1>
                    <p>Review your answers and learn from the results.</p>
                </div>
            </div>

            <div className="questions-list">
                {questions.map((question, index) => {
                    const userResponse = userAnswers.find(ua => ua.questionId === question.id);
                    const isSkipped = !userResponse || userResponse.selectedOptionId === null;
                    const isCorrect = userResponse?.isCorrect;

                    let cardStatusClass = "is-incorrect-card";
                    let statusClass = "tag-error";
                    let statusText = "Incorrect";

                    if (isSkipped) {
                        cardStatusClass = "is-skipped-card";
                        statusClass = "tag-skipped";
                        statusText = "Skipped";
                    } else if (isCorrect) {
                        cardStatusClass = "is-correct-card";
                        statusClass = "tag-success";
                        statusText = "Correct";
                    }

                    const clockConfig = question?.metadata?.clockConfig;

                    return (
                        <div key={question.id} className={`review-card ${cardStatusClass}`}>
                            <div className="card-top-bar">
                                <div className="q-meta">
                                    <span className="q-index">Question {index + 1}</span>
                                    <span className={`status-tag ${statusClass}`}>
                                        {statusText}
                                    </span>
                                </div>
                                <div className="time-badge">
                                    <Clock size={14} /> {userResponse?.timeSpentSeconds.toFixed(1)}s
                                </div>
                            </div>

                            {question.imageUrl && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    backgroundColor: '#f3f4f6', /* Light grey tone (equivalent to Tailwind gray-100) */
                                    padding: '16px',           /* Gives breathing room around the image */
                                    borderRadius: '12px'       /* Smooths out the box container corners */
                                }}>
                                    <img
                                        src={question.imageUrl}
                                        alt="Question visual layout"
                                        className="question-image"
                                        style={{
                                            maxHeight: '150px',
                                            width: 'auto',
                                            objectFit: 'contain',
                                            borderRadius: '8px', /* Optional: Rounds the corners slightly so the shadow looks cleaner */
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' /* Medium elegant shadow */
                                        }}
                                        loading="lazy"
                                    />
                                </div>
                            )}

                            <h3 className="question-text">
                                <span><QuestionText text={question.text} /></span>
                            </h3>
                            {clockConfig && (
                                <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0' }}>
                                    <AnalogClock
                                        hour={clockConfig.hour}
                                        minute={clockConfig.minute}
                                        size={220}
                                    />
                                </div>
                            )}

                            <div className="options-grid">
                                {question.Options.map((option) => {
                                    const isSelected = option.id === userResponse?.selectedOptionId;
                                    const isCorrectOption = option.isCorrect; // This comes from your Options table

                                    let optionClass = "option-box";

                                    // Logic: Always highlight the correct answer in green.
                                    // Logic: Only highlight the user's selection in red if it was WRONG.
                                    if (isCorrectOption) {
                                        optionClass += " is-correct";
                                    } else if (isSelected) {
                                        optionClass += " is-wrong";
                                    }

                                    return (
                                        <div key={option.id} className={optionClass}>
                                            <span className="option-text"><OptionText text={option.text} /></span>
                                            <div className="indicator-wrapper">
                                                {isCorrectOption && <span className="indicator-text success">✓ Correct Answer</span>}
                                                {isSelected && !isCorrectOption && <span className="indicator-text error">✕ Your Selection</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default QuizReview;